import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { PreFooterLinkGroup, PreFooterNewsletter, PreFooterSocialLink } from './types'

@safeCustomElement('mdc-footer')
export class MdcFooter extends LitElement {
  @property({ type: String }) tagline = ''
  @property({ type: String }) paragraph = ''

  // Pre-footer properties
  @property({ type: Object }) newsletter?: PreFooterNewsletter
  @property({ type: Array }) socialLinks?: PreFooterSocialLink[]
  @property({ type: Array }) linkGroups?: PreFooterLinkGroup[]
  @property({ type: String }) copyright = ''
  @property({ type: Boolean }) dark = false

  @state() private emailValue = ''

  static styles = css`
		:host {
			display: block;
			--mdc-footer-background: var(--color-functional-gray-black, #111111);
			--mdc-footer-color: var(--color-functional-gray-white, #ffffff);
			--mdc-footer-border-color: rgba(var(--color-functional-gray-black-rgb), 0.1);
			--mdc-footer-tagline-color: var(--mdc-footer-color);
			--mdc-footer-paragraph-color: rgba(255, 255, 255, 0.6);
			font-family: var(--typography-font-family-sans);
		}

		/* ---- Pre-footer styles ---- */

		/* Grid system (scoped for shadow DOM) */
		.mdc-grid {
			display: grid;
			grid-template-columns: repeat(12, minmax(0, 1fr));
			grid-row-gap: 1.5rem;
			grid-column-gap: 1.5rem;
			width: 100%;
			padding-left: 3rem;
			padding-right: 3rem;
			box-sizing: border-box;
			max-width: var(--container-max-width, 1440px);
			margin: 0 auto;
		}

		.mdc-grid .mdc-grid {
			padding-left: 0;
			padding-right: 0;
		}

		.span-2 {
			grid-column-end: span 2;
		}

		.span-4 {
			grid-column-end: span 4;
		}

		.start-1 {
			grid-column-start: 1;
		}

		.span-full {
			grid-column: 1 / span 12;
		}

		.mdc-pre-footer {
			padding-top: 2.5625rem;
			padding-bottom: 1.5rem;
			border-top: 1px solid var(--color-functional-gray-200, #cccccc);
			box-sizing: border-box;
		}

		.mdc-pre-footer__top {
			grid-row-gap: 0.75rem;
		}

		.mdc-pre-footer--dark {
			background-color: var(--color-functional-gray-900, #262626);
			color: var(--color-functional-gray-white, #ffffff);
		}

		.mdc-pre-footer--dark a,
		.mdc-pre-footer--dark a:visited {
			color: var(--color-functional-gray-white, #ffffff);
		}

		.mdc-pre-footer--light {
			background-color: var(--color-functional-gray-white, #ffffff);
			color: var(--color-functional-gray-900, #262626);
		}

		.mdc-pre-footer--light a,
		.mdc-pre-footer--light a:visited {
			color: var(--color-functional-gray-900, #262626);
		}

		.mdc-pre-footer__newsletter {
			padding-right: var(--spacing-150, 1.5rem);
		}

		.mdc-newsletter__title {
			color: var(--color-functional-gray-200, #cccccc);
			margin-bottom: 1rem;
			font-size: 0.75rem;
			font-weight: 450;
			line-height: 0.9375rem;
		}

		.mdc-newsletter__email {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			background: var(--color-functional-gray-white, #ffffff);
			border: 1px solid var(--color-functional-gray-200, #cccccc);
			border-radius: 0.5rem;
			width: 100%;
			position: relative;
			box-sizing: border-box;
		}

		.mdc-newsletter__email input {
			width: 100%;
			height: 100%;
			border: none;
			padding: 0.75rem 1rem;
			border-radius: 0.5rem;
			background: transparent;
			font-size: 0.875rem;
			box-sizing: border-box;
		}

		.mdc-newsletter__email input:focus {
			outline: none;
			border: 1px solid var(--color-functional-gray-300, #999999);
		}

		.mdc-newsletter__email input.error,
		.mdc-newsletter__email input.error:focus {
			border: 1px solid var(--color-functional-red, #bb2a33);
		}

		.mdc-newsletter__email button {
			position: absolute;
			background-color: transparent;
			border: none;
			white-space: nowrap;
			padding: 0 1rem;
			text-decoration: underline;
			right: 0.125rem;
			background: var(--color-functional-gray-white, #ffffff);
			cursor: pointer;
			font-size: 0.6875rem;
			line-height: 0.8125rem;
		}

		.mdc-newsletter__submit {
			font-size: 0.6875rem;
			line-height: 0.8125rem;
		}

		.mdc-newsletter__social-links {
			display: flex;
			width: 100%;
			justify-content: flex-start;
			align-items: center;
			padding: 2rem 0;
			gap: 2rem;
		}

		.mdc-social-link {
			text-decoration: none;
			transition: opacity 0.3s;
			color: inherit;
		}

		.mdc-social-link:hover {
			opacity: 0.5;
		}

		.mdc-link-group ul {
			list-style: none;
			padding-left: 0;
			margin: 0;
			margin-block-start: 0;
			margin-block-end: 0;
			padding-inline-start: 0;
			margin-inline-start: 0;
			margin-inline-end: 0;
		}

		.mdc-link-group p,
		.mdc-link-group li {
			font-size: 0.875rem;
			line-height: 1.125rem;
		}

		.mdc-link-group li {
			margin-bottom: 0.5rem;
		}

		.mdc-link-group__title {
			color: var(--color-functional-gray-200, #cccccc);
			margin-bottom: 1rem;
			font-size: 0.75rem;
			font-weight: 450;
			line-height: 0.9375rem;
		}

		.mdc-link-group a {
			text-decoration: none;
			letter-spacing: -0.01em;
			transition: opacity 0.3s;
		}

		.mdc-link-group a:hover {
			opacity: 0.5;
		}

		.mdc-copyright {
			font-size: 0.75rem;
			line-height: 0.9375rem;
			margin-top: 5.9375rem;
			color: var(--color-functional-gray-200, #cccccc);
		}

		.mdc-pre-footer__accordion {
			display: none;
		}

		/* breakpoints.lg - 1 */
		@media screen and (max-width: 1023px) {
			.mdc-grid {
				padding-left: 2.5rem;
				padding-right: 2.5rem;
			}

			.mdc-pre-footer {
				padding-top: 1.5rem;
				padding-bottom: 1.5rem;
			}

			.sm-span-full {
				grid-column: 1 / span 12;
			}

			.mdc-pre-footer__newsletter {
				padding-right: 0;
			}

			.mdc-pre-footer__newsletter .mdc-newsletter__title {
				margin-bottom: 0.5rem;
				font-size: 0.625rem;
				line-height: 0.75rem;
			}

			.mdc-pre-footer__newsletter .mdc-newsletter__email input {
				padding: 0.75rem;
				font-size: 0.875rem;
				line-height: 1.375rem;
			}

			.mdc-pre-footer__newsletter .mdc-newsletter__email button {
				font-size: 0.5625rem;
				line-height: 0.625rem;
				padding: 0 0.75rem;
			}

			.mdc-pre-footer__newsletter .mdc-newsletter__social-links {
				padding: 1rem 0;
			}

			.mdc-link-group {
				display: none;
			}

			.mdc-pre-footer__accordion {
				display: block;
			}

			.mdc-pre-footer__accordion .mdc-link-group__list {
				list-style: none;
				padding-left: 0;
				margin: 0;
			}

			.mdc-pre-footer__accordion .mdc-link-group__list li {
				margin-bottom: 0.5rem;
			}

			.mdc-pre-footer__accordion .mdc-link-group__list li .mdc-link {
				font-size: 0.625rem;
				line-height: 0.75rem;
			}

			.mdc-pre-footer .mdc-copyright {
				font-size: 0.5625rem;
				line-height: 0.6875rem;
				margin-top: 2rem;
			}
		}

		/* breakpoints.sm - 1 */
		@media screen and (max-width: 639px) {
			.mdc-grid {
				padding-left: 1rem;
				padding-right: 1rem;
			}
		}

		/* ---- Footer styles ---- */

		.mdc-footer {
			border-bottom: 1px solid var(--mdc-footer-border-color);
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: space-between;
			background-color: var(--mdc-footer-background);
			color: var(--mdc-footer-color);
			padding: 2.5rem 0;
			box-sizing: border-box;
		}

		.mdc-footer--sticky {
			position: sticky;
			z-index: -50;
			bottom: 0;
		}

		.mdc-footer__wrapper {
			display: flex;
			width: 100%;
			max-width: var(--container-max-width, 1440px);
			margin: 0 auto;
			padding-left: 3rem;
			padding-right: 3rem;
			box-sizing: border-box;
		}

		.mdc-footer__wrapper--top {
			justify-content: space-between;
			margin-bottom: 5rem;
		}

		.mdc-footer__tagline,
		.mdc-footer__paragraph {
			max-width: 341px;
		}

		.mdc-footer__tagline {
			font-size: 1.25rem;
			line-height: 1.5625rem;
			color: var(--mdc-footer-tagline-color);
		}

		.mdc-footer__paragraph {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			color: var(--mdc-footer-paragraph-color);
			font-size: 0.875rem;
			line-height: 1.125rem;
			text-align: left;
		}

		.mdc-footer__buttons {
			margin-top: 1.5rem;
		}

		.mdc-footer__buttons button:not(:last-child) {
			margin-right: 0.75rem;
		}

		/* breakpoints.lg - 1 */
		@media screen and (max-width: 1023px) {
			.mdc-footer__wrapper {
				padding-left: 2.5rem;
				padding-right: 2.5rem;
			}
		}

		/* breakpoints.md - 1 */
		@media screen and (max-width: 767px) {
			.mdc-footer {
				padding: 1.5rem 0;
			}

			.mdc-footer__wrapper {
				padding-left: 1rem;
				padding-right: 1rem;
			}

			.mdc-footer__wrapper--top {
				flex-direction: column;
			}

			.mdc-footer__tagline {
				font-size: 0.9375rem;
				line-height: 1.125rem;
				margin-bottom: 0.75rem;
			}

			.mdc-footer__paragraph {
				font-size: 0.875rem;
				line-height: 1.375rem;
			}

			.mdc-footer__buttons {
				margin-top: 0.75rem;
			}
		}
	`

  private handleEmailInput(e: Event) {
    const input = e.target as HTMLInputElement
    this.emailValue = input.value
  }

  private handleSubmitClick() {
    this.dispatchEvent(
      new CustomEvent('submit', {
        detail: { email: this.emailValue },
        bubbles: true,
        composed: true,
      })
    )
  }

  private renderPreFooter() {
    const hasPreFooter =
      this.newsletter ||
      (this.socialLinks && this.socialLinks.length > 0) ||
      (this.linkGroups && this.linkGroups.length > 0) ||
      this.copyright
    if (!hasPreFooter) return nothing

    const className = `mdc-pre-footer mdc-pre-footer--${this.dark ? 'dark' : 'light'}`

    return html`
			<div class=${className}>
				<div class="mdc-pre-footer__top mdc-grid">
					${
            this.newsletter || (this.socialLinks && this.socialLinks.length > 0)
              ? html`
								<div class="mdc-pre-footer__newsletter span-4 sm-span-full">
									${
                    this.newsletter
                      ? html`
											<div class="mdc-newsletter__title">${this.newsletter.title}</div>
											<div class="mdc-newsletter__email">
												<input
													class="mdc-newsletter__input"
													placeholder=${this.newsletter.emailLabel}
													.value=${this.emailValue}
													@input=${this.handleEmailInput}
												/>
												<button type="button" class="mdc-newsletter__submit" @click=${this.handleSubmitClick}>
													${this.newsletter.submitButton}
												</button>
											</div>
										`
                      : nothing
                  }
									${
                    this.socialLinks && this.socialLinks.length > 0
                      ? html`
												<div class="mdc-newsletter__social-links">
													${this.socialLinks.map(
                            (socialLink) => html`
															<a
																href=${socialLink.href}
																target="_blank"
																rel="noopener noreferrer"
																class="mdc-social-link"
																aria-label=${socialLink.label || socialLink.icon}
															>
																<mdc-icon name=${socialLink.icon} size="md"></mdc-icon>
															</a>
														`
                          )}
												</div>
											`
                      : nothing
                  }
								</div>
							`
              : nothing
          }
					${this.linkGroups?.map(
            (linkGroup) => html`
							<div class="mdc-link-group span-2 sm-span-full">
								<p class="mdc-link-group__title">${linkGroup.title}</p>
								<ul class="mdc-link-group__list span-2 sm-span-full">
									${linkGroup.links?.map(
                    (link) => html`
											<li>
												<mdc-link href=${link.url} type=${this.dark ? 'inverse-primary' : 'primary'} text=${link.label}></mdc-link>
											</li>
										`
                  )}
								</ul>
							</div>
						`
          )}
					<mdc-accordion class="mdc-pre-footer__accordion sm-span-full">
						${this.linkGroups?.map(
              (group, i) => html`
								<mdc-accordion-item
									id="pre-footer-group-${i}"
									label=${group.title}
									color=${this.dark ? 'inverse' : 'default'}
								>
									<ul class="mdc-link-group__list">
										${group.links?.map(
                      (link) => html`
												<li>
													<mdc-link href=${link.url} type=${this.dark ? 'inverse-primary' : 'primary'} text=${link.label}></mdc-link>
												</li>
											`
                    )}
									</ul>
								</mdc-accordion-item>
							`
            )}
					</mdc-accordion>
				</div>
				<div class="mdc-pre-footer__bottom mdc-grid">
					${
            this.copyright
              ? html`
								<p class="mdc-copyright span-4 start-1 sm-span-full">${this.copyright}</p>
							`
              : nothing
          }
				</div>
			</div>
		`
  }

  render() {
    return html`
			${this.renderPreFooter()}
			<footer class="mdc-footer">
				<div class="mdc-footer__wrapper mdc-footer__wrapper--top">
					<div class="mdc-footer__tagline mdc-title">${this.tagline}</div>
					<div class="mdc-footer__paragraph">
						${this.paragraph}
						<div class="mdc-footer__buttons">
							<mdc-button-group>
								<slot name="buttons"></slot>
							</mdc-button-group>
						</div>
					</div>
				</div>
				<div class="mdc-footer__wrapper mdc-footer__wrapper--bottom">
					<mdc-brand-logo size="lg" color="inverse"></mdc-brand-logo>
				</div>
			</footer>
		`
  }
}
